import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  canonical?: string;
}

export const SEO = ({
  title = "CalcLab - Free Science & Math Calculators | 100+ Tools",
  description = "100+ completely free science calculators forever. Physics, chemistry, finance, and math tools with step-by-step solutions. SuperCalc AI for complex equations. No subscriptions required.",
  keywords = "calculator, science calculator, physics calculator, chemistry calculator, math calculator, finance calculator, free calculator, online calculator, step by step solutions, LaTeX export, SuperCalc AI",
  image = "https://lovable.dev/opengraph-image-p98pqg.png",
  type = "website",
  canonical
}: SEOProps) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  
  // Always use the canonical domain for SEO purposes
  const baseUrl = 'https://calclab.app';
  const currentUrl = canonical || `${baseUrl}${location.pathname}`;
  
  // Supported languages with locale codes
  const languageLocales: Record<string, string> = {
    en: 'en_US',
    es: 'es_ES',
    fr: 'fr_FR',
    de: 'de_DE',
    zh: 'zh_CN',
    ja: 'ja_JP',
    pt: 'pt_BR',
    it: 'it_IT',
    ko: 'ko_KR',
    ar: 'ar_SA',
    hi: 'hi_IN',
    ru: 'ru_RU',
    nl: 'nl_NL',
    cs: 'cs_CZ',
    id: 'id_ID'
  };
  
  const currentLanguage = i18n.language || 'en';
  const currentLocale = languageLocales[currentLanguage] || 'en_US';

  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:locale', currentLocale, true);
    
    // Add alternate locales
    Object.entries(languageLocales).forEach(([lang, locale]) => {
      if (lang !== currentLanguage) {
        const metaName = `og:locale:alternate`;
        let element = document.querySelector(`meta[property="${metaName}"][content="${locale}"]`);
        if (!element) {
          element = document.createElement('meta');
          element.setAttribute('property', metaName);
          element.setAttribute('content', locale);
          document.head.appendChild(element);
        }
      }
    });

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);
    updateMetaTag('twitter:card', 'summary_large_image');

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', currentUrl);
    
    // Add hreflang links for all languages
    // Remove old hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    
    // Add hreflang for each supported language
    Object.keys(languageLocales).forEach(lang => {
      const hreflangLink = document.createElement('link');
      hreflangLink.setAttribute('rel', 'alternate');
      hreflangLink.setAttribute('hreflang', lang);
      hreflangLink.setAttribute('href', `${baseUrl}${location.pathname}`);
      document.head.appendChild(hreflangLink);
    });
    
    // Add x-default hreflang
    const xDefaultLink = document.createElement('link');
    xDefaultLink.setAttribute('rel', 'alternate');
    xDefaultLink.setAttribute('hreflang', 'x-default');
    xDefaultLink.setAttribute('href', `${baseUrl}${location.pathname}`);
    document.head.appendChild(xDefaultLink);
    
    // Add language meta tag
    updateMetaTag('language', currentLanguage);
    document.documentElement.lang = currentLanguage;
  }, [title, description, keywords, image, type, currentUrl, currentLanguage, currentLocale, location.pathname]);

  return null;
};

export const addStructuredData = (data: object) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(data);
  
  // Remove existing structured data script if present
  const existing = document.querySelector('script[type="application/ld+json"]');
  if (existing) {
    existing.remove();
  }
  
  document.head.appendChild(script);
  
  return () => {
    script.remove();
  };
};
